<script>
    import { onMount, afterUpdate, onDestroy } from 'svelte';
    import { currentUser, selectedItem, questioning, question, chatHistory, questionsAsked } from '../store/store.js';
    import { getFileNameId } from '../utils/utils.js';
    import Login from './common/Login.svelte';

    // --- Förslag på frågor ---
    const allSuggestedQuestions = [
        "Vilka större underhåll är planerade de närmaste åren?",
        "Har avgiften höjts nyligen eller finns planer på att höja den?",
        "Finns det några hyresintäkter?",
        "Hur mycket amorterade föreningen förra året?",
        "Är föreningen äkta eller oäkta?",
        "Finns det några pågående tvister?",
        "Finns det en skuld och tillgångsanalys?"
    ];

    let suggestedQuestions = [];
    let chatHistoryContainer;
    let initialMessageTime = ""; // Klockslag för det första meddelandet

    let maxQuestions;
    let questionsLeft;

    $: {
        if ($currentUser && $currentUser.subscription) {
            const tier = $currentUser.subscription.tier;
            if (tier === 'Pro' || tier === 'Plus') {
                maxQuestions = 5;
            } else {
                maxQuestions = 2;
            }
        } else {
            maxQuestions = 2;
        }
        questionsLeft = maxQuestions - $questionsAsked;
    }

    let thinkingDotsInterval;
    let currentThinkingDots = ""; // For the "..." animation
    let typewriterInterval;

    function startThinkingIndicator() {
        clearInterval(thinkingDotsInterval);
        currentThinkingDots = ".";
        let dotCount = 1;
        thinkingDotsInterval = setInterval(() => {
            dotCount = (dotCount % 3) + 1;
            currentThinkingDots = ".".repeat(dotCount);
        }, 400);
    }

    function stopThinkingIndicator() {
        clearInterval(thinkingDotsInterval);
        currentThinkingDots = "";
    }

    function startTypewriter(messageId, fullText) {
        clearInterval(typewriterInterval);
        let charIndex = 0;
        
        // Ensure the target message content is initially empty
        chatHistory.update(hist => {
            const msgIndex = hist.findIndex(m => m.id === messageId);
            if (msgIndex !== -1) {
                hist[msgIndex].content = "";
            }
            return [...hist];
        });

        typewriterInterval = setInterval(() => {
            if (charIndex < fullText.length) {
                chatHistory.update(hist => {
                    const msgIndex = hist.findIndex(m => m.id === messageId);
                    if (msgIndex !== -1) {
                        // Append character by character
                        hist[msgIndex].content += fullText[charIndex];
                    }
                    return [...hist]; // Trigger Svelte's reactivity
                });
                charIndex++;
            } else {
                clearInterval(typewriterInterval);
            }
        }, 20); // Adjust typing speed (milliseconds per character)
    }

    // Hantera byte av BRF och initiera chatten
    $: if ($selectedItem) {
        const brfName = $selectedItem.brf_name;
        
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const currentTime = `${hours}:${minutes}`;
        initialMessageTime = currentTime; // Store for the first message
        questionsAsked.set(0);

        const initialGreetingContent = `Hej! Jag är BoBot. Vad vill du veta om ${brfName}?`;
        const initialGreetingId = 'assistant-initial-' + Date.now();
        
        chatHistory.set([{
            role: 'assistant',
            content: "", 
            id: initialGreetingId,
            isInitialGreeting: true,
            isLoading: false,
            timestamp: currentTime // Add timestamp to the initial message object
        }]);
        question.set("");

        setTimeout(() => {
            startTypewriter(initialGreetingId, initialGreetingContent);
        }, 50); 
    }

    onMount(() => {
        // Slumpa och välj ut 3 frågor när komponenten mountas
        suggestedQuestions = allSuggestedQuestions
            .sort(() => 0.5 - Math.random())
            .slice(0, 3);
        
        // Om $selectedItem redan är satt när komponenten mountas,
        // kommer den reaktiva $: blocken ovan att hantera initieringen av chatten.
        // Om $selectedItem sätts senare, triggas den också då.
    });

    afterUpdate(() => {
        if (chatHistoryContainer) {
            // Scroll to bottom, but only if the user isn't scrolled up significantly
            // This prevents auto-scroll from yanking the view if the user is reading old messages.
            const threshold = 10; // Increased threshold
            if (chatHistoryContainer.scrollHeight - chatHistoryContainer.scrollTop <= chatHistoryContainer.clientHeight + threshold) {
                chatHistoryContainer.scrollTop = chatHistoryContainer.scrollHeight;
            }
        }
    });

    onDestroy(() => {
        // Clear intervals when component is destroyed
        clearInterval(thinkingDotsInterval);
        clearInterval(typewriterInterval);
    });

    // Fetch data from your backend
	async function askQuestion(currentQuestionText, fileName, stadgarFileName, historyPayload, thinkingMessageId) {
		questioning.set(true);

		try {
			const fileNameId = getFileNameId(fileName);
			const url = `https://boanalys-backend-324523837899.europe-north1.run.app/ask_question`;
            const payload = {
                question: currentQuestionText.toString(),
                file_name: fileNameId,
                stadgar_file_name: stadgarFileName,
                history: historyPayload // Send history *before* current user question and thinking bubble
            };

			const response = await fetch(url, {
			    method: 'POST',
			    headers: {
				    'Authorization': `Bearer ${$currentUser?.accessToken}`,
                    'Content-Type': 'application/json'
			    },
                credentials: 'include',
                body: JSON.stringify(payload)
			});

			if (!response.ok) {
                let errorDetail = 'Något gick fel vid hämtning av svar.';
                try {
                    const errorData = await response.json();
                    if (errorData && errorData.detail) {
                        errorDetail = errorData.detail;
                    }
                } catch (e) {
                    // Response was not JSON or other error parsing it
                }
			    throw new Error(errorDetail);
			}

			const data = await response.json();
            stopThinkingIndicator();

            // The backend's response includes the new answer.
            // We'll use data.data.answer for the typewriter.
            // The data.data.history can be used to ensure consistency if needed,
            // but here we'll animate the message already in our store.
            const newAnswerText = data.data.answer;
            const now = new Date();
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');
            const answerTimestamp = `${hours}:${minutes}`;

            chatHistory.update(hist => {
                const msgIndex = hist.findIndex(m => m.id === thinkingMessageId);
                if (msgIndex !== -1) {
                    hist[msgIndex].isLoading = false; 
                    hist[msgIndex].timestamp = answerTimestamp; // Add timestamp to the answer message
                }
                return [...hist];
            });
            startTypewriter(thinkingMessageId, newAnswerText);
			
		} catch (error) {
			console.error('Error asking question:', error);
            stopThinkingIndicator();
            // Remove the temporary thinking message on error
            chatHistory.update(hist => hist.filter(msg => msg.id !== thinkingMessageId));
            
            const currentHist = $chatHistory; // Get potentially updated history
            const now = new Date();
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');
            const errorTimestamp = `${hours}:${minutes}`;
            
            const errorMsg = { 
                role: 'assistant', 
                content: `Ett fel uppstod: ${error.message}`, 
                id: 'err-' + Date.now(),
                timestamp: errorTimestamp // Add timestamp to the error message
            };
            
            if (currentHist.length > 0 && currentHist[currentHist.length -1].isInitialGreeting) {
                 chatHistory.update(hist => {
                    let lastIsGreeting = hist[hist.length-1]?.isInitialGreeting;
                    if(lastIsGreeting) return [...hist, errorMsg]; // Add after initial if it's the only one
                    // This case might need refinement if initial greeting is complexly handled
                    return [...hist.slice(0,-1), {...hist[hist.length-1], isInitialGreeting: false}, errorMsg];
                 });
            } else {
                chatHistory.update(hist => [...hist, errorMsg]);
            }
		} finally {
			questioning.set(false);
		}
	}

    function handleSubmit() {
        if (!$question.trim() || $questioning || questionsLeft <= 0) return;

        questionsAsked.update(n => n + 1);

        const userQuestionText = $question;
        // History for backend: current history without any greeting flags
        const historyForBackend = $chatHistory.map(msg => {
            const { isInitialGreeting, isLoading, ...rest } = msg; // Remove UI-specific flags
            return rest;
        }).filter(msg => msg.content || msg.role === 'user'); // Ensure no empty content from previous thinking bubbles

        // 1. Add user's message to UI
        chatHistory.update(hist => [...hist, { 
            role: 'user', 
            content: userQuestionText, 
            id: 'user-' + Date.now() 
        }]);

        // 2. Add placeholder "thinking" message for BoBot to UI
        const thinkingMsgId = 'assistant-thinking-' + Date.now();
        chatHistory.update(hist => [...hist, { 
            role: 'assistant', 
            content: "", // Content will be the animated dots, not stored here
            isLoading: true, 
            id: thinkingMsgId,
            isInitialGreeting: false // Explicitly not an initial greeting
        }]);
        
        startThinkingIndicator(); // Start "..." animation
        question.set(""); // Clear input field

        askQuestion(userQuestionText, $selectedItem.file_name, $selectedItem.stadgar_file_name, historyForBackend, thinkingMsgId);
    }

    // --- Ny funktion för att hantera klick på förslag ---
    function handleSuggestionClick(suggestion) {
        if (questionsLeft <= 0) return;
        question.set(suggestion); // Sätt frågan i store
        handleSubmit();         // Skicka frågan, will use current $chatHistory
    }
    // --- Slut ny funktion ---

    function formatText(text) {
		return text
		.replace(/\n/g, "<br>") // Byt ut radbrytningar mot <br>
		.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"); // Byt ut **text** mot <strong>text</strong>
	}
</script>

<div class="key-value-table box double-width chat-wrapper">
    {#if $currentUser}
    <div class="questions-left-indicator">
        {questionsLeft > 0 ? `${questionsLeft} av ${maxQuestions} frågor kvar` : "Frågor slut"}
    </div>
    {/if}
    {#if !$currentUser}
        <div class="login-overlay">
            <Login isOverlay={true} />
        </div>
    {/if}

<div class="chat-inner-container" class:dimmed={!$currentUser}>
    <table>
        <tbody>
            <tr>
                    <td class="chat-cell">
                        <div class="chat-area-wrapper">
                            <!-- Chat history display -->
                            <div class="chat-history-display" bind:this={chatHistoryContainer}>
                                {#each $chatHistory as message (message.id)}
                                    <div class="message {message.role}">
                                        {#if message.role === 'user'}
                                            <p>{@html formatText(message.content)}</p>
                                        {:else if message.role === 'assistant'}
                                            <img src="/BoBot/BoBot-happy.svg" alt="BoBot" class="bobot-avatar">
                                            <div class="assistant-message-content">
                                                <div class="assistant-message-header">
                                                    <span class="assistant-name">BoBot</span>
                                                    {#if message.timestamp && !message.isLoading}
                                                        <span class="message-timestamp">{message.timestamp}</span>
                                                    {/if}
                                                </div>
                                                <div class="assistant-message-body">
                                                    {#if message.isLoading}
                                                        <span class="thinking-dots">{@html currentThinkingDots}</span>
                                                    {:else}
                                                        {@html formatText(message.content)}
                                                    {/if}
                                                </div>
                                            </div>
                                        {/if}
                                    </div>
                                {/each}

                                <!-- Suggested questions displayed after the initial greeting -->
                                {#if $chatHistory.length === 1 && $chatHistory[0].isInitialGreeting && !$questioning && suggestedQuestions.length > 0}
                                    <div class="initial-suggestions-after-greeting">
                                        <!-- Optional: <p class="suggestions-lead-in">Här är några förslag:</p> -->
                                        <div class="suggestions">
                                            {#each suggestedQuestions as suggestion}
                                                <button
                                                    class="suggestion-button"
                                                    on:click={() => handleSuggestionClick(suggestion)}
                                                >
                                                    {suggestion}
                                                </button>
                                            {/each}
                                        </div>
                                    </div>
                                {/if}
                            </div>
                        </div>

                        <!-- Question input form at the bottom -->
                        <form on:submit|preventDefault={handleSubmit} class="question-form">
                            <input type="text" class="question-input" bind:value={$question} placeholder={questionsLeft > 0 ? "Ställ en fråga till BoBot" : "Du har nått max antal frågor. Läs mer på din profils sida."} disabled={$questioning || questionsLeft <= 0} />
                        </form>
                    </td>
                </tr>
            </tbody>
    </table>
</div>
</div>

<style>
    .chat-wrapper {
        position: relative;
        min-height: 450px;
    }

    .chat-inner-container,
    .chat-inner-container > table {
        height: 100%;
    }

    .chat-cell {
        display: flex;
        flex-direction: column;
        height: 100%; /* Make chat-cell fill its parent TR */
    }

    .chat-area-wrapper {
        flex-grow: 1; /* Allows this area to take up available space, pushing form down */
        overflow: hidden; /* Prevent this wrapper from scrolling, chat-history-display will scroll */
        display: flex;
        flex-direction: column;
        /* justify-content: flex-end; /* REMOVED or change to flex-start */
    }

    .chat-history-display {
        overflow-y: auto;
        padding: 10px;
        max-height: 490px; /* Chat content scrolls after this height */
        width: 100%; /* Ensure it takes full width within chat-area-wrapper */
        box-sizing: border-box; /* Ensures padding and border are included in max-height */
    }

    /* Custom Scrollbar Styles */
    .chat-history-display::-webkit-scrollbar {
        width: 8px; /* Width of the scrollbar */
    }

    .chat-history-display::-webkit-scrollbar-track {
        background: #f1f1f1; /* Color of the track */
        border-radius: 4px;
    }

    .chat-history-display::-webkit-scrollbar-thumb {
        background: #888; /* Color of the scroll thumb */
        border-radius: 4px;
    }

    .chat-history-display::-webkit-scrollbar-thumb:hover {
        background: #555; /* Color of the scroll thumb on hover */
    }

    /* For Firefox */
    .chat-history-display {
        scrollbar-width: thin; /* "auto" or "thin" */
        scrollbar-color: #888 #f1f1f1; /* thumb and track color */
    }

    .message {
        margin-bottom: 12px;
        display: flex;
        align-items: flex-start; /* Align avatar and bubble to the top */
    }

    .message.user {
        justify-content: flex-end;
    }
    .message.user p {
        background-color: var(--box-title-color);
        padding: 10px 14px;
        margin-right: 10px;
        border-radius: 18px 18px 18px 18px; /* Slightly more modern bubble */
        max-width: 80%;
        word-wrap: break-word;
        line-height: 1.4;
        color: white;
    }

    .bobot-avatar {
        width: 32px;
        height: 32px;
        margin-right: 8px;
        flex-shrink: 0; /* Prevent avatar from shrinking */
        margin-top: 2px; /* Fine-tune vertical alignment with bubble */
    }

    .message.assistant {
        justify-content: flex-start;
    }

    .assistant-message-content { /* Replaces .question-answer for assistant messages */
        background-color: #f8f9fa;
        padding: 10px 14px;
        border-radius: 18px 18px 18px 18px;
        max-width: 80%;
        word-wrap: break-word;
        line-height: 1.4;
        display: flex;
        flex-direction: column;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .assistant-message-header {
        display: flex;
        align-items: baseline; /* Aligns name and timestamp nicely */
        margin-bottom: 5px; /* Space between header and body */
    }

    .assistant-name {
        font-weight: bold;
        color: #333; /* Darker than timestamp for emphasis */
        font-size: 0.95em; /* Slightly smaller if desired, or 1em */
    }

    .message-timestamp {
        font-size: 0.8em;
        color: #777;
        margin-left: 8px; /* Space between name and timestamp */
    }

    .assistant-message-body {
        /* Text within the body will inherit line-height etc. from .assistant-message-content */
        font-size: 1em; /* Ensure body text is standard size */
        min-height: 1.4em; /* Ensure bubble has some height even when empty or just dots */
    }
    
    .assistant-message-body p { /* If you wrap content in <p> via formatText */
        margin: 0;
    }

    .thinking-dots {
        display: inline-block;
        /* Styling for the dots, e.g., color, font-weight */
        color: #555;
        font-weight: bold;
    }
    
    .initial-suggestions-after-greeting {
        margin-top: 10px; /* Space above suggestions */
        margin-bottom: 10px; /* Space below suggestions */
        padding-left: calc(32px + 8px); /* Align with assistant message content (avatar width + margin) */
    }

    .initial-suggestions-after-greeting .suggestions {
        justify-content: flex-start; /* Align suggestions to the left */
        /* gap, flex-wrap etc. are inherited from global .suggestions */
    }

    .question-form {
        padding: 8px 6px;
        background-color: #fff; /* Cleaner background */
        border-top: 1px solid #e0e0e0;
        flex-shrink: 0; /* Prevent form from shrinking */
        margin-bottom: -20px; /* Ensure no extra margin at the bottom */
        position: relative;
    }
    .question-form input.question-input {
        width: 100%;
        padding: 12px 18px;
        border: 1px solid #d0d0d0;
        border-radius: 22px; /* More rounded input */
        box-sizing: border-box;
        font-size: 1em;
    }
    .question-form input.question-input:disabled {
        background-color: #f8f9fa;
        cursor: not-allowed;
    }
    .question-form input.question-input:focus {
        outline: none;
        border-color: #007bff; /* Highlight on focus */
        box-shadow: 0 0 0 2px rgba(0,123,255,.25);
    }

    .suggestions {
        justify-content: center;
        margin-top: 15px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px; /* Slightly reduced gap */
    }
    .suggestion-button {
        background-color: #f8f9fa;
        border-radius: 16px; /* More rounded buttons */
        padding: 8px 14px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        border: 1px solid #e0e0e0; /* Softer border */
        font-size: 0.85em;
        cursor: pointer;
        transition: transform 0.15s, box-shadow 0.15s, background-color 0.15s;
        text-align: center;
        color: #333;
    }
    .suggestion-button:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
        background-color: #f1f3f5;
    }
    .questions-left-indicator {
        position: absolute;
        top: 15px;
        right: 20px;
        font-size: 0.8em;
        color: #777;
        z-index: 1;
    }

    .login-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.8); /* Semi-transparent white overlay */
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10;
        border-radius: 10px;
    }

    .dimmed {
        filter: blur(1px); /* Apply a blur effect to the chat interface */
        pointer-events: none; /* Disable pointer events on the blurred interface */
    }
</style>